// services/interviewTrackingService.js
import { db } from "./firebase";
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import OrganizationService from "./OrganizationService";
import { sendEmail } from "./emailService";

/**
 * Service for tracking and managing interview sessions in Firebase
 */
class InterviewTrackingService {
  /**
   * Records the start of an interview session
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} userId - The current user's ID
   * @param {string} role - The user's role ("interviewer" or "candidate")
   * @param {Object} settings - The interview settings
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async recordInterviewStart(meetingId, userId, role, settings = {}) {
    try {
      if (!meetingId || !userId) {
        console.error("Missing required parameters for recordInterviewStart");
        return false;
      }

      // Get interview name from settings or use default
      const interviewName = settings.interviewName || "Untitled Interview";

      console.log(settings);

      // Create metadata record if it doesn't exist
      const metadataRef = doc(db, "rooms", meetingId, "metadata", "info");

      await setDoc(
        metadataRef,
        {
          status: "active",
          startTime: serverTimestamp(),
          interviewName,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      // Add to user's interviews collection
      const userInterviewRef = doc(
        db,
        "users",
        userId,
        "interviews",
        meetingId
      );

      await setDoc(
        userInterviewRef,
        {
          interviewName,
          meetingId,
          date: serverTimestamp(),
          role,
          status: "active",
          hasReport: false,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      console.log(
        `Interview start recorded for meeting ${meetingId} and user ${userId}`
      );

      return true;
    } catch (error) {
      console.error("Error recording interview start:", error);
      return false;
    }
  }

  /**
   * Records the end of an interview session
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} userId - The current user's ID
   * @param {number} durationMinutes - Duration in minutes (optional)
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async recordInterviewEnd(meetingId, userId, durationMinutes = null) {
    try {
      if (!meetingId) {
        console.error("Missing meeting ID for recordInterviewEnd");
        return false;
      }

      // Update room metadata
      const metadataRef = doc(db, "rooms", meetingId, "metadata", "info");
      const endTime = new Date();
      const updates = {
        status: "completed",
        endTime: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      // Calculate duration if not provided
      if (!durationMinutes) {
        try {
          const metadataDoc = await getDoc(metadataRef);
          if (metadataDoc.exists() && metadataDoc.data().startTime) {
            const startTime = metadataDoc.data().startTime.toDate();
            durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
            updates.duration = durationMinutes;
          }
        } catch (err) {
          console.error("Error calculating duration:", err);
        }
      } else {
        updates.duration = durationMinutes;
      }

      await updateDoc(metadataRef, updates);

      // Update user's interview record if userId provided
      if (userId) {
        const userInterviewRef = doc(
          db,
          "users",
          userId,
          "interviews",
          meetingId
        );
        const userDoc = await getDoc(userInterviewRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          await updateDoc(userInterviewRef, {
            status: "completed",
            duration: durationMinutes || 0,
            lastUpdated: serverTimestamp(),
          });

          // Also update the organization's interview record if this interview belongs to an organization
          if (userData.organizationId) {
            const orgInterviewRef = doc(
              db,
              "organizations",
              userData.organizationId,
              "interviews",
              meetingId
            );
            const orgDoc = await getDoc(orgInterviewRef);

            if (orgDoc.exists()) {
              await updateDoc(orgInterviewRef, {
                status: "completed",
                duration: durationMinutes || 0,
                lastUpdated: serverTimestamp(),
              });
            }
          }
        }
      }

      // Update all participants' records for this interview to completed
      try {
        const participantsQuery = query(
          collection(db, "rooms", meetingId, "participants")
        );

        const participantsSnapshot = await getDocs(participantsQuery);

        for (const participantDoc of participantsSnapshot.docs) {
          const participantId = participantDoc.id;
          const participantData = participantDoc.data();

          // Skip if it's the current user (already updated above)
          if (participantId === userId) continue;

          // Check if this participant has an interview record
          const participantInterviewRef = doc(
            db,
            "users",
            participantId,
            "interviews",
            meetingId
          );
          const participantInterviewDoc = await getDoc(participantInterviewRef);

          if (participantInterviewDoc.exists()) {
            await updateDoc(participantInterviewRef, {
              status: "completed",
              duration: durationMinutes || 0,
              lastUpdated: serverTimestamp(),
            });
          }
        }
      } catch (err) {
        console.error("Error updating participant records:", err);
      }

      console.log(`Interview end recorded for meeting ${meetingId}`);

      return true;
    } catch (error) {
      console.error("Error recording interview end:", error);
      return false;
    }
  }

  /**
   * Get past interviews for a user
   *
   * @param {string} userId - The user's ID
   * @param {number} limit - Maximum number of interviews to fetch (default: 10)
   * @returns {Promise<Array>} - Array of interview data
   */
  async getPastInterviews(userId, maxResults = 10) {
    try {
      if (!userId) {
        console.error("Missing user ID for getPastInterviews");
        return [];
      }

      const interviewsQuery = query(
        collection(db, "users", userId, "interviews"),
        where("status", "==", "completed"),
        orderBy("date", "desc"),
        limit(maxResults)
      );

      const interviews = [];
      const querySnapshot = await getDocs(interviewsQuery);

      for (const doc of querySnapshot.docs) {
        const interviewData = doc.data();

        // Format dates for display
        let formattedDate = "Unknown date";
        if (interviewData.date) {
          try {
            const date = interviewData.date.toDate();
            formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          } catch (err) {
            console.warn("Error formatting date:", err);
          }
        }

        // Format duration
        let formattedDuration = "Unknown duration";
        if (interviewData.duration) {
          formattedDuration = `${interviewData.duration} minute${
            interviewData.duration !== 1 ? "s" : ""
          }`;
        }

        interviews.push({
          id: doc.id,
          title: interviewData.interviewName || "Untitled Interview",
          date: formattedDate,
          rawDate: interviewData.date,
          duration: formattedDuration,
          rawDuration: interviewData.duration,
          role: interviewData.role || "unknown",
          status: interviewData.status || "unknown",
          hasReport: interviewData.hasReport || false,
          score: interviewData.score || null,
          candidateName: interviewData.candidateName || "Unknown Candidate",
          candidateEmail: interviewData.candidateEmail || null,
          interviewerName:
            interviewData.interviewerName || "Unknown Interviewer",

          // Add candidate status tracking fields
          candidateStatus: interviewData.candidateStatus || "pending",
          candidateStatusUpdatedAt: interviewData.candidateStatusUpdatedAt
            ? interviewData.candidateStatusUpdatedAt.toDate()
            : null,

          // Add next round tracking fields
          hasNextRound: interviewData.hasNextRound || false,
          nextRoundId: interviewData.nextRoundId || null,
          nextRoundScheduledDate: interviewData.nextRoundScheduledDate
            ? interviewData.nextRoundScheduledDate.toDate()
            : null,

          // Add round tracking fields
          roundNumber: interviewData.roundNumber || 1,
          previousRoundId: interviewData.previousRoundId || null,
        });
      }

      return interviews;
    } catch (error) {
      console.error("Error fetching past interviews:", error);
      return [];
    }
  }

  /**
   * Get upcoming scheduled interviews for a user
   *
   * @param {string} userId - The user's ID
   * @param {number} limit - Maximum number of interviews to fetch (default: 5)
   * @returns {Promise<Array>} - Array of interview data
   */
  async getUpcomingInterviews(userId, maxResults = 5) {
    try {
      if (!userId) {
        console.error("Missing user ID for getUpcomingInterviews");
        return [];
      }

      const interviewsQuery = query(
        collection(db, "users", userId, "interviews"),
        where("status", "==", "scheduled"),
        orderBy("scheduledDate", "asc"),
        limit(maxResults)
      );

      const interviews = [];
      const querySnapshot = await getDocs(interviewsQuery);

      for (const doc of querySnapshot.docs) {
        const interviewData = doc.data();

        // Format dates for display
        let formattedDate = "Unknown date";
        let formattedTime = "Unknown time";

        if (interviewData.scheduledDate) {
          try {
            const date = interviewData.scheduledDate.toDate();
            formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });

            formattedTime = date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
          } catch (err) {
            console.warn("Error formatting scheduled date:", err);
          }
        }

        interviews.push({
          id: doc.id,
          meetingId: doc.id,
          title: interviewData.interviewName || "Untitled Interview",
          date: formattedDate,
          time: formattedTime,
          rawDate: interviewData.scheduledDate,
          role: interviewData.role || "unknown",
          candidate:
            interviewData.candidateName ||
            interviewData.candidateEmail ||
            "Unknown Candidate",
          interviewer: interviewData.interviewerName || "Unknown Interviewer",

          // Add round tracking fields
          roundNumber: interviewData.roundNumber || 1,
          previousRoundId: interviewData.previousRoundId || null,
          candidateEmail: interviewData.candidateEmail || null,
        });
      }

      return interviews;
    } catch (error) {
      console.error("Error fetching upcoming interviews:", error);
      return [];
    }
  }

  /**
   * Get a specific interview's details
   *
   * @param {string} meetingId - The meeting ID
   * @returns {Promise<Object|null>} - Interview data or null if not found
   */
  async getInterviewDetails(meetingId) {
    try {
      if (!meetingId) {
        console.error("Missing meeting ID for getInterviewDetails");
        return null;
      }

      // Get room metadata
      const metadataRef = doc(db, "rooms", meetingId, "metadata", "info");
      const metadataDoc = await getDoc(metadataRef);

      if (!metadataDoc.exists()) {
        console.warn(`No metadata found for interview ${meetingId}`);
        return null;
      }

      const metadata = metadataDoc.data();

      // Get settings
      const settingsRef = doc(db, "rooms", meetingId, "settings", "info");
      const settingsDoc = await getDoc(settingsRef);
      const settings = settingsDoc.exists() ? settingsDoc.data() : {};

      // Get participants
      const participantsQuery = query(
        collection(db, "rooms", meetingId, "participants")
      );

      const participantsSnapshot = await getDocs(participantsQuery);
      const participants = [];

      for (const participantDoc of participantsSnapshot.docs) {
        participants.push({
          id: participantDoc.id,
          ...participantDoc.data(),
        });
      }

      return {
        id: meetingId,
        metadata,
        settings,
        participants,
      };
    } catch (error) {
      console.error("Error fetching interview details:", error);
      return null;
    }
  }

  /**
   * Update interview participant information
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} userId - The user's ID
   * @param {Object} participantInfo - Additional participant info
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async updateParticipantInfo(meetingId, userId, participantInfo) {
    try {
      if (!meetingId || !userId) {
        console.error("Missing required parameters for updateParticipantInfo");
        return false;
      }

      // Update user's interview record
      const userInterviewRef = doc(
        db,
        "users",
        userId,
        "interviews",
        meetingId
      );

      await updateDoc(userInterviewRef, {
        ...participantInfo,
        lastUpdated: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error("Error updating participant info:", error);
      return false;
    }
  }

  /**
   * Generate or update an interview report
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} userId - The user's ID
   * @param {Object} reportData - Report content
   * @returns {Promise<string|null>} - Report ID or null if failed
   */
  async generateReport(meetingId, userId, reportData) {
    try {
      if (!meetingId || !userId || !reportData) {
        console.error("Missing required parameters for generateReport");
        return null;
      }

      // Get user's role to verify they're the interviewer
      const userInterviewRef = doc(
        db,
        "users",
        userId,
        "interviews",
        meetingId
      );
      const userInterviewDoc = await getDoc(userInterviewRef);

      if (!userInterviewDoc.exists()) {
        console.error("User interview record not found");
        return null;
      }

      const userInterviewData = userInterviewDoc.data();

      // Only interviewers can generate reports
      if (userInterviewData.role !== "interviewer") {
        console.error("Only interviewers can generate reports");
        return null;
      }

      // Create or update report
      const reportRef = doc(db, "rooms", meetingId, "reports", "main");

      await setDoc(
        reportRef,
        {
          ...reportData,
          generatedBy: userId,
          generatedAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      // Update user interview record to indicate report exists
      await updateDoc(userInterviewRef, {
        hasReport: true,
        lastUpdated: serverTimestamp(),
      });

      // Update participants' records
      try {
        const participantsQuery = query(
          collection(db, "rooms", meetingId, "participants")
        );

        const participantsSnapshot = await getDocs(participantsQuery);

        for (const participantDoc of participantsSnapshot.docs) {
          const participantId = participantDoc.id;

          // Skip if it's the current user (already updated above)
          if (participantId === userId) continue;

          // Check if this participant has an interview record
          const participantInterviewRef = doc(
            db,
            "users",
            participantId,
            "interviews",
            meetingId
          );
          const participantInterviewDoc = await getDoc(participantInterviewRef);

          if (participantInterviewDoc.exists()) {
            await updateDoc(participantInterviewRef, {
              hasReport: true,
              lastUpdated: serverTimestamp(),
            });
          }
        }
      } catch (err) {
        console.error("Error updating participant records for report:", err);
      }

      return "main"; // Return the report ID
    } catch (error) {
      console.error("Error generating report:", error);
      return null;
    }
  }

  /**
   * Get an interview report
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} reportId - The report ID (default: 'main')
   * @returns {Promise<Object|null>} - Report data or null if not found
   */
  async getReport(meetingId, reportId = "main") {
    try {
      if (!meetingId) {
        console.error("Missing meeting ID for getReport");
        return null;
      }

      const reportRef = doc(db, "rooms", meetingId, "reports", reportId);
      const reportDoc = await getDoc(reportRef);

      if (!reportDoc.exists()) {
        console.warn(`No report found for interview ${meetingId}`);
        return null;
      }

      return {
        id: reportId,
        ...reportDoc.data(),
      };
    } catch (error) {
      console.error("Error fetching report:", error);
      return null;
    }
  }

  // We need to add a new method to the interviewTrackingService.js file
  // Add this method to the InterviewTrackingService class

  /**
   * Records a scheduled interview
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} candidateEmail - The candidate's email
   * @param {Date} scheduledDate - The scheduled date and time
   * @param {Object} settings - Additional interview settings
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async recordInterviewScheduled(
    meetingId,
    candidateEmail,
    scheduledDate,
    settings = {}
  ) {
    try {
      if (!meetingId || !candidateEmail || !scheduledDate) {
        console.error(
          "Missing required parameters for recordInterviewScheduled"
        );
        return false;
      }

      // Get interview name from settings or use default
      const interviewName = settings.interviewName || "Scheduled Interview";
      const candidateName =
        settings.candidateName || candidateEmail.split("@")[0];

      // Create metadata record if it doesn't exist
      const metadataRef = doc(db, "rooms", meetingId, "metadata", "info");

      await setDoc(
        metadataRef,
        {
          status: "scheduled",
          scheduledDate: scheduledDate,
          createdAt: serverTimestamp(),
          interviewName,
          candidateEmail,
          candidateName,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      // Check if the user who created the interview is authenticated
      // and add to their interviews collection
      const auth = getAuth();
      if (auth.currentUser) {
        const interviewerRef = doc(
          db,
          "users",
          auth.currentUser.uid,
          "interviews",
          meetingId
        );

        await setDoc(
          interviewerRef,
          {
            interviewName,
            meetingId,
            scheduledDate: scheduledDate,
            role: "interviewer",
            status: "scheduled",
            hasReport: false,
            candidateEmail,
            candidateName,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp(),
          },
          { merge: true }
        );
      }

      console.log(
        `Interview scheduled for meeting ${meetingId} with candidate ${candidateEmail}`
      );

      return true;
    } catch (error) {
      console.error("Error recording scheduled interview:", error);
      return false;
    }
  }

  /**
   * Save interview notes
   *
   * @param {string} meetingId - The meeting ID
   * @param {string} userId - The user's ID
   * @param {string} notes - The notes content
   * @param {string} userRole - The user's role (interviewer or candidate)
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async saveInterviewNotes(meetingId, userId, notes, userRole) {
    try {
      if (!meetingId || !userId || !notes.trim()) {
        console.error("Missing required parameters for saveInterviewNotes");
        return false;
      }

      // Update user's interview record with notes
      const userInterviewRef = doc(
        db,
        "users",
        userId,
        "interviews",
        meetingId
      );
      const userInterviewDoc = await getDoc(userInterviewRef);

      if (userInterviewDoc.exists()) {
        // Update existing interview record with notes field
        await updateDoc(userInterviewRef, {
          notes: notes,
          hasNotes: true,
          lastUpdated: serverTimestamp(),
        });
      }

      return true;
    } catch (error) {
      console.error("Error saving interview notes:", error);
      return false;
    }
  }

  /**
   * Update the candidate status for an interview
   *
   * @param {string} userId - The interviewer's user ID
   * @param {string} interviewId - The interview ID
   * @param {string} candidateStatus - The status ('pending', 'proceed', or 'rejected')
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async updateCandidateStatus(userId, interviewId, candidateStatus) {
    try {
      if (!userId || !interviewId || !candidateStatus) {
        console.error("Missing required parameters for updateCandidateStatus");
        return false;
      }

      // Only allow valid status values
      const validStatuses = ["pending", "proceed", "rejected"];
      if (!validStatuses.includes(candidateStatus)) {
        console.error(`Invalid candidate status: ${candidateStatus}`);
        return false;
      }

      // Get reference to the interview document
      const interviewRef = doc(db, "users", userId, "interviews", interviewId);

      // Update the document with the new status
      await updateDoc(interviewRef, {
        candidateStatus: candidateStatus,
        candidateStatusUpdatedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      });

      console.log(
        `Candidate status updated to ${candidateStatus} for interview ${interviewId}`
      );
      return true;
    } catch (error) {
      console.error("Error updating candidate status:", error);
      return false;
    }
  }


  async scheduleNextRound(previousInterview, nextRoundData, organizationId) {
    try {
      if (!previousInterview || !nextRoundData) {
        console.error("Missing required parameters for scheduleNextRound");
        return null;
      }
  
      if (!nextRoundData.date || !nextRoundData.time || !nextRoundData.title || !nextRoundData.selectedInterviewerId) {
        console.error("Missing required next round data properties");
        return null;
      }

      let organization = null;
      if (organizationId) {
        try {
          const orgRef = doc(db, "organizations", organizationId);
          const orgDoc = await getDoc(orgRef);
          if (orgDoc.exists()) {
            organization = { id: organizationId, ...orgDoc.data() };
          } else {
            console.warn(`Organization with ID ${organizationId} not found`);
          }
        } catch (orgError) {
          console.error("Error fetching organization data:", orgError);
        }
      }

      const newMeetingId = `meeting_${Date.now().toString(36)}`;

      const scheduledDateTime = new Date(`${nextRoundData.date}T${nextRoundData.time}:00`);
      const scheduledEndDateTime = new Date(scheduledDateTime.getTime() + 60 * 60 * 1000);
      const durationMinutes = 60;

      const selectedInterviewerId = nextRoundData.selectedInterviewerId;

      const interviewerName = nextRoundData.interviewerName || "Unknown Interviewer";
      const interviewerEmail = nextRoundData.interviewerEmail || "";

      const nextRoundNumber = (previousInterview.roundNumber || 1) + 1;

      const roundId = `round-${nextRoundNumber}`;

      const interviewSettings = {
        interviewName: nextRoundData.title,
        description: `Next round interview for ${previousInterview.candidateName || 'candidate'}`,
        organizationId: organization?.id || null,
        organizationName: organization?.name || null,
        scheduledBy: {
          uid: selectedInterviewerId,
          email: interviewerEmail || previousInterview.interviewerEmail,
          name: interviewerName || previousInterview.interviewerName || "Interviewer",
        },
        scheduledDate: scheduledDateTime,
        scheduledEndDate: scheduledEndDateTime,
        duration: durationMinutes,
        features: {
          summarize: true,
          followupPrompts: true,
          cheatingDetection: true,
        },
        candidateInfo: {
          email: previousInterview.candidateEmail || "",
          name: previousInterview.candidateName || "Candidate",
        },
        interviewerInfo: {
          uid: selectedInterviewerId,
          email: interviewerEmail,
        },
        positionTitle: previousInterview.positionTitle || "Position",
        status: "scheduled",
        jobListingId: previousInterview.jobListingId || null,
        roundId: roundId
      };

      const roomRef = doc(db, "rooms", newMeetingId);
      await setDoc(roomRef, { 
        lastUpdated: serverTimestamp(),
        settings: interviewSettings
      });

      const interviewData = {
        meetingId: newMeetingId,
        interviewName: nextRoundData.title,
        title: nextRoundData.title,
        scheduledDate: scheduledDateTime,
        scheduledEndDate: scheduledEndDateTime,
        duration: durationMinutes,
        status: "scheduled",
        candidateEmail: previousInterview.candidateEmail || "",
        candidateName: previousInterview.candidateName || "Candidate",
        interviewerId: selectedInterviewerId,
        interviewerEmail: interviewerEmail,
        interviewerName: interviewerName,
        organizationId: organization?.id || null,
        organizationName: organization?.name || null,
        createdAt: serverTimestamp(),
        createdBy: selectedInterviewerId,
        positionTitle: previousInterview.positionTitle || "Position",
        description: `Next round interview for ${previousInterview.candidateName || 'candidate'}`,
        jobListingId: previousInterview.jobListingId || null,
        traceId: `${previousInterview.jobListingId}-${previousInterview.meetingId}`,
        roundId: roundId,
        roundNumber: nextRoundNumber,
        // Next round specific fields
        isPreviousRound: true,
        previousRoundId: previousInterview.meetingId,
      };

      if (organization?.id) {
        const orgInterviewRef = doc(db, "organizations", organization.id, "interviews", newMeetingId);
        await setDoc(orgInterviewRef, interviewData);
      }

      if (organization?.id && (previousInterview.id || previousInterview.meetingId)) {
        const previousId = previousInterview.id || previousInterview.meetingId;
        const orgPreviousInterviewRef = doc(db, "organizations", organization.id, "interviews", previousId);

        try {
        const orgPreviousInterviewDoc = await getDoc(orgPreviousInterviewRef);
        
        if (orgPreviousInterviewDoc.exists()) {
          await updateDoc(orgPreviousInterviewRef, {
            hasNextRound: true,
            nextRoundId: newMeetingId,
            nextRoundScheduledDate: scheduledDateTime,
            lastUpdated: serverTimestamp(),
          });
        }
        } catch (error) {
          console.warn("Could not update previous interview record:", error);
        }
      }

      // 6. Send email invitations (similar to main scheduling flow)
      try {
        // Format date for email
        const formattedDate = scheduledDateTime.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
        });

        // Format time for display
        const formattedTime = scheduledDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        // Create meeting invitation content for candidate
        const candidateEmailContent = `
        Hi ${previousInterview.candidateName || previousInterview.candidateEmail?.split("@")[0]},
        
        You've been invited to a next round interview on ${formattedDate} at ${formattedTime}.
        
        Position: ${previousInterview.positionTitle || "Position"}
        ${organization?.name ? `Organization: ${organization.name}` : ""}
        Round: ${nextRoundNumber}
        
        Meeting ID: ${newMeetingId}
        
        Click here to join: https://resolut.co.in/meeting/${newMeetingId}
        
        Best regards,
        Resolut Team
      `;

        // Create meeting invitation content for interviewer
        const interviewerEmailContent = `
        Hi ${interviewerName},
        
        You've been assigned to conduct a next round interview on ${formattedDate} at ${formattedTime}.
        
        Position: ${previousInterview.positionTitle || "Position"}
        Candidate: ${previousInterview.candidateName || previousInterview.candidateEmail?.split("@")[0]} (${previousInterview.candidateEmail})
        Round: ${nextRoundNumber}
        Previous Round ID: ${previousInterview.id || previousInterview.meetingId}
        
        Meeting ID: ${newMeetingId}
        
        Click here to join: https://resolut.co.in/meeting/${newMeetingId}
        
        Best regards,
        Resolut Team
      `;

        // Send emails (assuming you have a sendEmail function)
        if (typeof sendEmail === 'function') {
          sendEmail({
            email: previousInterview.candidateEmail,
            subject: `Next Round: ${nextRoundData.title}`,
            text: candidateEmailContent,
        });

          console.log(interviewerEmail);

          sendEmail({
            email: interviewerEmail,
            subject: `[Interviewer Assignment] Next Round: ${nextRoundData.title}`,
            text: interviewerEmailContent,
          });
        }
      } catch (emailError) {
        console.warn("Error sending email invitations:", emailError);
      // Continue anyway - we've already saved the interview data
      }

      console.log(`Next round interview scheduled with meeting ID: ${newMeetingId}`);
      return newMeetingId;
    } catch (error) {
      console.error("Error scheduling next round interview:", error);
      return null;
    }
  }

  async getOrganizationInterviews(organizationId, status = null) {
    try {
      if (!organizationId) {
        console.error("Missing organization ID");
        return [];
      }

      // Helper function to format dates
      const formatDate = (dateField, defaultTime = "Unknown time") => {
        if (!dateField) {
          return {
            formattedDate: "Unknown date",
            formattedTime: defaultTime,
            rawDate: null
          };
        }

        try {
          const date = dateField instanceof Date ? dateField : dateField.toDate();

          return {
            formattedDate: date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
            formattedTime: date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
            rawDate: date
          };
        } catch (err) {
          console.warn("Error formatting date:", err);
          return {
            formattedDate: "Unknown date",
            formattedTime: defaultTime,
            rawDate: null
          };
        }
      };

      // Create query for organization's interviews
      let interviewsQuery;

      if (status) {
        interviewsQuery = query(
          collection(db, "organizations", organizationId, "interviews"),
          where("status", "==", status),
          orderBy("scheduledDate", "desc")
        );
      } else {
        interviewsQuery = query(
          collection(db, "organizations", organizationId, "interviews"),
          orderBy("scheduledDate", "desc")
        );
      }

      const querySnapshot = await getDocs(interviewsQuery);

      const interviews = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return interviews;
    } catch (error) {
      console.error("Error fetching organization interviews:", error);
      return [];
    }
  }

  async rescheduleInterview(meetingId, newScheduledDate, settings = {}) {
    try {
      if (!meetingId || !newScheduledDate) {
        console.error("Missing required parameters for rescheduleInterview");
        return false;
      }

      // Calculate scheduled end date
      let scheduledEndDate = newScheduledDate;
      if (settings.endTime && settings.date) {
        scheduledEndDate = new Date(`${settings.date}T${settings.endTime}`);
      } else {
        // Default to 30 minutes after start time
        scheduledEndDate = new Date(newScheduledDate.getTime() + 30 * 60 * 1000);
      }

      // Format date and time for emails
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      };

      const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      };

      const formattedDate = formatDate(newScheduledDate);
      const formattedTime = formatTime(newScheduledDate);

      // Update rooms collection
      const roomUpdateData = {
        lastUpdated: serverTimestamp(),
        'settings.scheduledDate': newScheduledDate,
        'settings.scheduledEndDate': scheduledEndDate,
      };

      if (settings.interviewerEmail) {
        roomUpdateData['settings.interviewerInfo.email'] = settings.interviewerEmail;
      }
      if (settings.interviewerUid) {
        roomUpdateData['settings.interviewerInfo.uid'] = settings.interviewerUid;
      }

      const meetingDocRef = doc(db, "rooms", meetingId);
      await updateDoc(meetingDocRef, roomUpdateData);

      // Update organization collection
      if (settings.orgId) {
        const orgInterviewRef = doc(
          db,
          "organizations",
          settings.orgId,
          "interviews",
          meetingId
        );

        const orgInterviewUpdateData = {
          scheduledDate: newScheduledDate,
          scheduledEndDate: scheduledEndDate,
          lastUpdated: serverTimestamp(),
        };

        if (settings.interviewerEmail) {
          orgInterviewUpdateData.interviewerEmail = settings.interviewerEmail;
        }
        if (settings.interviewerUid) {
          orgInterviewUpdateData.interviewerId = settings.interviewerUid;
        }
        if (settings.interviewerName) {
          orgInterviewUpdateData.interviewerName = settings.interviewerName;
        }

        await updateDoc(orgInterviewRef, orgInterviewUpdateData);
      }

      // Send email notifications
      const candidateName = settings.candidateName || settings.candidateEmail?.split("@")[0] || "Candidate";

      const candidateEmailContent = `
Hi ${candidateName},

Your interview has been rescheduled to ${formattedDate} at ${formattedTime}.

Interview Details:
• Title: ${settings.interviewName || "Technical Interview"}
• Date: ${formattedDate}
• Time: ${formattedTime}
• Duration: ${Math.round((scheduledEndDate - newScheduledDate) / (1000 * 60))} minutes

Click here to join: https://resolut.co.in/meeting/${meetingId}

Please make sure you're available at the new time. If you have any questions or concerns, please don't hesitate to reach out.

Best regards,
Resolut Team
    `.trim();

      const interviewerEmailContent = `
Hi there,

You have been assigned to conduct an interview that has been rescheduled to ${formattedDate} at ${formattedTime}.

Interview Details:
• Candidate: ${candidateName}
• Email: ${settings.candidateEmail || "Not provided"}
• Title: ${settings.interviewName || "Technical Interview"}
• Date: ${formattedDate}
• Time: ${formattedTime}
• Duration: ${Math.round((scheduledEndDate - newScheduledDate) / (1000 * 60))} minutes

Click here to join: https://resolut.co.in/meeting/${meetingId}

Please review any relevant candidate information before the interview.

Best regards,
Resolut Team
    `.trim();

      // Send emails if function is available
      if (typeof sendEmail === 'function') {
        // Send to candidate
        if (settings.candidateEmail) {
          await sendEmail({
            email: settings.candidateEmail,
            subject: `Interview Rescheduled - ${formattedDate} at ${formattedTime}`,
            text: candidateEmailContent,
        });
      }

        // Send to interviewer
        if (settings.interviewerEmail) {
          await sendEmail({
            email: settings.interviewerEmail,
            subject: `Interview Assignment - ${formattedDate} at ${formattedTime}`,
            text: interviewerEmailContent,
          });
        }
      }

      console.log(`Interview ${meetingId} successfully rescheduled to ${formattedDate} at ${formattedTime}`);
      return true;

    } catch (error) {
      console.error("Error rescheduling interview:", error);
      return false;
    }
  }

  async updateInterviewStatus(interviewId, status, organizationId = null) {
    try {
      if (!interviewId || !status) {
        console.error("Missing required parameters for updateInterviewStatus");
        return false;
      }

      const validStatuses = ["pending", "completed", "pending_feedback"];
      if (!validStatuses.includes(status)) {
        console.error(`Invalid interview status: ${status}`);
        return false;
      }

      const updates = {
        status: status
      };

      if (organizationId) {
        try {
          const orgInterviewRef = doc(db, "organizations", organizationId, "interviews", interviewId);
          const orgInterviewDoc = await getDoc(orgInterviewRef);

          if (orgInterviewDoc.exists()) {
            await updateDoc(orgInterviewRef, updates);
            console.log(`Organization interview status updated to ${status} for interview ${interviewId}`);
          }
        } catch (orgError) {
          console.warn("Could not update organization interview record:", orgError);
        }
      }

      return true;
    } catch (error) {
      console.error("Error updating interview status:", error);
      return false;
    }
  }

  async deleteInterview(interviewId, organizationId = null) {
    try {
      if (!interviewId) {
        console.error("Missing interview ID for deleteInterview");
        return false;
      }

      if (!organizationId) {
        console.error("Missing organisation ID for deleteInterview");
        return false;
      }

      // Get the current user
      const auth = getAuth();
      if (!auth.currentUser) {
        console.error("User must be authenticated to delete an interview");
        return false;
      }

      if (organizationId) {
        try {
          const orgInterviewRef = doc(db, "organizations", organizationId, "interviews", interviewId);
          await deleteDoc(orgInterviewRef);
        } catch (orgError) {
          console.warn("Could not delete organization interview record:", orgError);
        }
      }


      console.log(`Successfully deleted interview: ${interviewId}`);
      return true;

    } catch (error) {
      console.error("Error deleting interview:", error);
      return false;
    }
  }
}

export default new InterviewTrackingService();
